import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { getClientUser } from '~/shared/helpers/client-user-helpers';
import type { ShortUserType } from '~/shared/types/user-type';
import { LOCALES } from '~/universal/constants';

type ConfirmModalState = Readonly<{
  onTrigger: (() => void | Promise<void>) | null;
  title: string;
  message: string;
  isShowed: boolean;
  isDisabledSubmitButton: boolean;
  isRemoving: boolean;
  itemKey: string | null;
}>;

type ModalState = Readonly<{
  isDisabledSubmitButton: boolean;
  isShowed: boolean;
  isNoSubmitButton?: boolean;
  modalTitle?: string | null;
  wrapClassName?: string | null;
  modalContent: unknown;
}>;

type ChatStatusState = Readonly<{
  isOpen: boolean;
  _id: string | null;
  isCommonDialog: boolean;
}>;

type ChatState = Readonly<{
  isNewMessage: boolean;
  selectedDialog: unknown;
  selectedEntity: unknown;
  repliedMessage: unknown;
  currentDialog: unknown;
  isDialogs: boolean;
  chatStatus: ChatStatusState;
}>;

type SurveyEditorState = Readonly<{
  isSurveyEditor: boolean;
  isSurveyEditCreateForm: boolean;
  isSurveyResetForm: boolean;
  isSurveyPreviewMode: boolean;
  surveyFormTemplateId: string | null;
  surveyFormTemplate: unknown;
}>;

type FilesManagerState = Readonly<{
  isSharedFilesMode: boolean;
  isSharedFiles: boolean;
  isUpdateFilesList: boolean;
  isFilesManager: boolean;
  isSharedFolderMode: boolean;
  isFilesManagerNoAttachMode: boolean;
  editableInitialExercise: unknown;
  sharedBreadCrumbs: unknown[];
  selectedFiles: unknown[];
  personalBreadCrumbs: unknown[];
  creatableFile: unknown;
  isDocReaderShowed: boolean;
  selectedFile: unknown;
  sharedFolder: unknown;
  personalCourseCategoryFolder: unknown;
  sharedCourseCategoryFolder: unknown;
  personalFolder: unknown;
  editableFile: unknown;
  isPersonalFiles: boolean;
}>;

type AppSliceType = Readonly<{
  user: ShortUserType | null;
  modal: ModalState;
  stateOfConfirmModal: ConfirmModalState;
  chat: ChatState;
  surveyEditor: SurveyEditorState;
  filesManager: FilesManagerState;
  lng: string;
}>;

export const initialStateOfConfirmModal: ConfirmModalState = {
  onTrigger: null,
  title: '',
  message: '',
  isShowed: false,
  isDisabledSubmitButton: false,
  isRemoving: false,
  itemKey: null,
};

export const initialStateOfModal: ModalState = {
  isDisabledSubmitButton: false,
  isShowed: false,
  wrapClassName: null,
  isNoSubmitButton: false,
  modalContent: null,
};

const initialState: AppSliceType = {
  user: typeof window === 'undefined' ? null : getClientUser(),
  modal: initialStateOfModal,
  stateOfConfirmModal: initialStateOfConfirmModal,
  chat: {
    selectedDialog: null,
    currentDialog: null,
    selectedEntity: null,
    repliedMessage: null,
    isNewMessage: false,
    isDialogs: false,
    chatStatus: {
      isOpen: false,
      _id: null,
      isCommonDialog: false,
    },
  },
  surveyEditor: {
    isSurveyEditor: false,
    isSurveyEditCreateForm: false,
    isSurveyResetForm: false,
    isSurveyPreviewMode: false,
    surveyFormTemplateId: null,
    surveyFormTemplate: null,
  },
  filesManager: {
    sharedBreadCrumbs: [],
    editableInitialExercise: null,
    personalBreadCrumbs: [],
    selectedFiles: [],
    isDocReaderShowed: false,
    isUpdateFilesList: false,
    isSharedFiles: true,
    isFilesManager: false,
    isSharedFolderMode: false,
    isFilesManagerNoAttachMode: false,
    isSharedFilesMode: false,
    editableFile: null,
    personalCourseCategoryFolder: null,
    selectedFile: null,
    sharedCourseCategoryFolder: null,
    sharedFolder: null,
    personalFolder: null,
    creatableFile: null,
    isPersonalFiles: true,
  },
  lng: LOCALES.UA,
};

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<ShortUserType | null>) => {
      state.user = action.payload;
    },

    setCreatableFile: (state, action: PayloadAction<unknown>) => {
      state.filesManager.creatableFile = action.payload;
    },
    setIsSharedFolderMode: (state, action: PayloadAction<boolean>) => {
      state.filesManager.isSharedFolderMode = action.payload;
    },
    setChatStatus: (state, action: PayloadAction<ChatStatusState>) => {
      state.chat.chatStatus = action.payload;
    },
    setIsNewMessage: (state, action: PayloadAction<boolean>) => {
      state.chat.isNewMessage = action.payload;
    },
    setSelectedDialog: (state, action: PayloadAction<unknown>) => {
      state.chat.selectedDialog = action.payload;
    },
    changeStateOfConfirmModal: (state, action: PayloadAction<ConfirmModalState>) => {
      state.stateOfConfirmModal = action.payload;
    },
    setRepliedMessage: (state, action: PayloadAction<unknown>) => {
      state.chat.repliedMessage = action.payload;
    },
    setSelectedEntity: (state, action: PayloadAction<unknown>) => {
      state.chat.selectedEntity = action.payload;
    },
    setCurrentDialog: (state, action: PayloadAction<unknown>) => {
      state.chat.currentDialog = action.payload;
    },
    setIsDialogs: (state, action: PayloadAction<boolean>) => {
      state.chat.isDialogs = action.payload;
    },
    setEditableFile: (state, action: PayloadAction<unknown>) => {
      state.filesManager.editableFile = action.payload;
    },
    setEditableInitialExercise: (state, action: PayloadAction<unknown>) => {
      state.filesManager.editableInitialExercise = action.payload;
    },
    setIsDocReaderShowed: (state, action: PayloadAction<boolean>) => {
      state.filesManager.isDocReaderShowed = action.payload;
    },
    setIsFilesManager: (state, action: PayloadAction<boolean>) => {
      state.filesManager.isFilesManager = action.payload;
    },
    setIsUpdateFilesList: (state, action: PayloadAction<boolean>) => {
      state.filesManager.isUpdateFilesList = action.payload;
    },
    setIsSharedFiles: (state, action: PayloadAction<boolean>) => {
      state.filesManager.isSharedFiles = action.payload;
    },
    setSharedFolder: (state, action: PayloadAction<unknown>) => {
      state.filesManager.sharedFolder = action.payload;
    },
    setIsFilesManagerNoAttachMode: (state, action: PayloadAction<boolean>) => {
      state.filesManager.isFilesManagerNoAttachMode = action.payload;
    },
    setPersonalCourseCategoryFolder: (state, action: PayloadAction<unknown>) => {
      state.filesManager.personalCourseCategoryFolder = action.payload;
    },
    setSharedCourseCategoryFolder: (state, action: PayloadAction<unknown>) => {
      state.filesManager.sharedCourseCategoryFolder = action.payload;
    },
    setPersonalFolder: (state, action: PayloadAction<unknown>) => {
      state.filesManager.personalFolder = action.payload;
    },
    setSelectedFile: (state, action: PayloadAction<unknown>) => {
      state.filesManager.selectedFile = action.payload;
    },
    setSharedBreadCrumbs: (state, action: PayloadAction<unknown[]>) => {
      state.filesManager.sharedBreadCrumbs = action.payload;
    },
    setPersonalBreadCrumbs: (state, action: PayloadAction<unknown[]>) => {
      state.filesManager.personalBreadCrumbs = action.payload;
    },
    setIsSharedFilesMode: (state, action: PayloadAction<boolean>) => {
      state.filesManager.isSharedFilesMode = action.payload;
    },
    setSelectedFiles: (state, action: PayloadAction<unknown[]>) => {
      state.filesManager.selectedFiles = action.payload;
    },
    setLanguage: (state, action: PayloadAction<string>) => {
      state.lng = action.payload;
    },
    setIsSurveyEditor: (state, action: PayloadAction<boolean>) => {
      state.surveyEditor.isSurveyEditor = action.payload;
    },
    setIsSurveyPreviewMode: (state, action: PayloadAction<boolean>) => {
      state.surveyEditor.isSurveyPreviewMode = action.payload;
    },
    setIsPersonalFiles: (state, action: PayloadAction<boolean>) => {
      state.filesManager.isPersonalFiles = action.payload;
    },
    setIsSurveyEditCreateForm: (state, action: PayloadAction<boolean>) => {
      state.surveyEditor.isSurveyEditCreateForm = action.payload;
    },
    setSurveyFormTemplate: (state, action: PayloadAction<unknown>) => {
      state.surveyEditor.surveyFormTemplate = action.payload;
    },
    setSurveyFormTemplateId: (state, action: PayloadAction<string | null>) => {
      state.surveyEditor.surveyFormTemplateId = action.payload;
    },
    setIsSurveyResetForm: (state, action: PayloadAction<boolean>) => {
      state.surveyEditor.isSurveyResetForm = action.payload;
    },
    changeStateOfModal: (state, action: PayloadAction<ModalState>) => {
      state.modal = action.payload;
    },
    onShowFilesManager: (state, action: PayloadAction<boolean>) => {
      state.filesManager.isFilesManager = action.payload;
    },
  },
});

export const {
  setIsSurveyEditor,
  setChatStatus,
  setSelectedEntity,
  setIsSharedFilesMode,
  changeStateOfConfirmModal,
  setSharedFolder,
  setIsNewMessage,
  setIsDialogs,
  setIsDocReaderShowed,
  setSelectedFiles,
  setPersonalCourseCategoryFolder,
  setCurrentDialog,
  setIsSharedFiles,
  setRepliedMessage,
  setIsUpdateFilesList,
  setSelectedFile,
  setSelectedDialog,
  setSharedCourseCategoryFolder,
  setEditableInitialExercise,
  setPersonalFolder,
  onShowFilesManager,
  setSharedBreadCrumbs,
  setPersonalBreadCrumbs,
  setCreatableFile,
  setIsFilesManager,
  setEditableFile,
  setIsFilesManagerNoAttachMode,
  setIsSurveyResetForm,
  setIsSharedFolderMode,
  setIsPersonalFiles,
  setSurveyFormTemplate,
  setSurveyFormTemplateId,
  setIsSurveyPreviewMode,
  setIsSurveyEditCreateForm,
  changeStateOfModal,
  setUser,
  setLanguage,
} = appSlice.actions;
